import { NavLink } from "react-router-dom";
import {
    LayoutDashboard,
    Package,
    ClipboardList,
    ShoppingCart,
    Target,
    Ticket,
    MapPin,
    Bell,
    Sparkles,
    Building2,
    Store,
    Truck,
    LogOut,
    Upload,
    FileBarChart,
    Percent,
    Tag,
    ClipboardCheck,
    GitCompareArrows,
    ShieldCheck,
    LayoutGrid,
    UserCog,
    Network,
    Warehouse,
    CalendarClock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

const ROLE_LABEL = {
    retailer: "Retailer / Outlet",
    distributor: "Distributor",
    asm: "ASM",
    rsm: "RSM",
    state_manager: "State / Zone / Mgmt",
    spykar_admin: "Spykar Admin",
    system_admin: "System Admin",
    admin: "Admin",
    executive: "Executive",
    planner: "Planner",
    sales_manager: "Sales Manager",
    field_executive: "Field Executive",
};

const NAV = [
    { to: "/", label: "Executive Home", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
    { to: "/sellin", label: "Sell-In", icon: Truck, testid: "nav-sellin" },
    { to: "/sellout", label: "Sell-Out Hub", icon: ShoppingCart, testid: "nav-sellout" },
    { to: "/targets", label: "Targets", icon: Target, testid: "nav-targets" },
    { to: "/schemes", label: "Schemes", icon: Ticket, testid: "nav-schemes" },
    { to: "/visits", label: "Field Visits", icon: MapPin, testid: "nav-visits" },
    { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
    { to: "/copilot", label: "Copilot", icon: Sparkles, testid: "nav-copilot" },
];

const MASTERS = [
    { to: "/masters/products", label: "Products", icon: ClipboardList, testid: "nav-products" },
    { to: "/masters/stores", label: "Stores", icon: Store, testid: "nav-stores" },
    { to: "/masters/distributors", label: "Distributors", icon: Building2, testid: "nav-distributors" },
];

const REPORTS = [
    { to: "/reports/upload", label: "Upload Center", icon: Upload, testid: "nav-reports-upload" },
    { to: "/reports/tertiary", label: "Tertiary Sales", icon: FileBarChart, testid: "nav-reports-tertiary" },
    { to: "/reports/dsr", label: "Daily Sales (DSR)", icon: ShoppingCart, testid: "nav-reports-dsr" },
    { to: "/reports/offers", label: "POS Offers", icon: Tag, testid: "nav-reports-offers" },
    { to: "/reports/eoss", label: "EOSS Working", icon: Percent, testid: "nav-reports-eoss" },
    { to: "/reports/gujrat-dsr", label: "DSR FORM", icon: ClipboardList, testid: "nav-reports-gujrat-dsr" },
];

const SPYKAR = [
    { to: "/spykar/daily-sales", label: "Daily Sales Capture", icon: ClipboardCheck, testid: "nav-spykar-daily" },
    { to: "/spykar/reconciliation", label: "Daily vs POS Recon", icon: GitCompareArrows, testid: "nav-spykar-recon" },
    { to: "/spykar/discount-compliance", label: "Discount Compliance", icon: ShieldCheck, testid: "nav-spykar-compliance" },
    { to: "/spykar/pos-formats", label: "POS Formats", icon: LayoutGrid, testid: "nav-spykar-formats" },
];

const ADMIN_NAV = [
    { to: "/admin/users", label: "Users & Access", icon: UserCog, testid: "nav-admin-users" },
    { to: "/admin/hierarchy", label: "Hierarchy", icon: Network, testid: "nav-admin-hierarchy" },
    { to: "/admin/masters", label: "Master Data", icon: LayoutGrid, testid: "nav-admin-masters" },
    { to: "/admin/store-master", label: "Store Master", icon: Warehouse, testid: "nav-admin-store-master" },
    { to: "/admin/eoss-config", label: "EOSS Config", icon: CalendarClock, testid: "nav-admin-eoss-config" },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    return (
        <aside
            className="flex h-screen w-60 shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground"
            data-testid="app-sidebar"
        >
            {/* Brand */}
            <div className="flex h-14 items-center gap-2 border-b border-white/5 px-4">
                <img
                    src="/brand/fundle_logo_white.png"
                    alt="Fundle"
                    className="h-6 w-auto"
                    data-testid="sidebar-brand-logo"
                />
                <div className="ml-1 leading-tight">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-white/50">
                        MBO Operating System
                    </div>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto px-2 py-3">
                <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                    Operate
                </div>
                {NAV.map((n) => (
                    <SideLink key={n.to} {...n} />
                ))}
                <div className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                    Master Data
                </div>
                {MASTERS.map((n) => (
                    <SideLink key={n.to} {...n} />
                ))}
                <div className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                    Reports
                </div>
                {REPORTS.map((n) => (
                    <SideLink key={n.to} {...n} />
                ))}
                <div className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                    Spykar Ops
                </div>
                {SPYKAR.map((n) => (
                    <SideLink key={n.to} {...n} />
                ))}
                {ADMIN_ROLES.has(user?.role) && (
                    <>
                        <div className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                            Admin
                        </div>
                        {ADMIN_NAV.map((n) => (
                            <SideLink key={n.to} {...n} />
                        ))}
                    </>
                )}
            </nav>

            {/* User */}
            <div className="border-t border-white/5 p-3">
                <div className="mb-2 rounded-sm bg-white/[0.04] p-2.5">
                    <div className="text-[13px] font-medium">{user?.name}</div>
                    <div className="mt-0.5 text-[11px] uppercase tracking-wider text-white/50">
                        {ROLE_LABEL[user?.role] || user?.role?.replace("_", " ")}
                    </div>
                </div>
                <button
                    onClick={logout}
                    data-testid="logout-btn"
                    className="flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-[13px] text-white/70 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
            </div>
        </aside>
    );
}

function SideLink({ to, label, icon: Icon, testid }) {
    return (
        <NavLink
            to={to}
            end={to === "/"}
            data-testid={testid}
            className={({ isActive }) =>
                [
                    "group mb-0.5 flex items-center gap-2.5 rounded-sm px-3 py-1.5 text-[13px] transition-colors duration-150",
                    isActive
                        ? "bg-white/[0.08] text-white"
                        : "text-white/65 hover:bg-white/[0.05] hover:text-white",
                ].join(" ")
            }
        >
            {({ isActive }) => (
                <>
                    <span
                        className={
                            "h-3.5 w-[2px] rounded-full transition-colors " +
                            (isActive ? "bg-primary" : "bg-transparent")
                        }
                    />
                    <Icon className="h-3.5 w-3.5" />
                    <span className="truncate">{label}</span>
                </>
            )}
        </NavLink>
    );
}
